import { useMemo } from 'react';
import { cn } from '@/lib/cn';

type Palette = 'violet' | 'rose-violet' | 'emerald' | 'warm' | 'neutral';

interface BarStackProps {
  values: number[];
  palette?: Palette;
  height?: number;
  highlight?: number | null;
  className?: string;
}

const GRADIENTS: Record<Palette, string[]> = {
  violet: [
    'hsl(268 90% 78%)',
    'hsl(262 85% 72%)',
    'hsl(258 82% 66%)',
    'hsl(254 78% 62%)',
    'hsl(250 74% 58%)',
  ],
  'rose-violet': [
    'hsl(328 84% 76%)',
    'hsl(308 82% 74%)',
    'hsl(288 80% 72%)',
    'hsl(268 78% 68%)',
    'hsl(248 76% 64%)',
  ],
  emerald: [
    'hsl(170 75% 75%)',
    'hsl(160 70% 60%)',
    'hsl(152 72% 52%)',
    'hsl(145 70% 46%)',
    'hsl(138 68% 40%)',
  ],
  warm: [
    'hsl(38 94% 70%)',
    'hsl(28 92% 64%)',
    'hsl(18 88% 60%)',
    'hsl(8 86% 58%)',
    'hsl(352 84% 60%)',
  ],
  neutral: [
    'hsl(240 5% 82%)',
    'hsl(240 5% 74%)',
    'hsl(240 5% 66%)',
    'hsl(240 5% 56%)',
    'hsl(240 6% 44%)',
  ],
};

export function BarStack({
  values,
  palette = 'violet',
  height = 48,
  highlight,
  className,
}: BarStackProps) {
  const colors = GRADIENTS[palette];
  const bars = useMemo(() => {
    if (!values.length) return [];
    const max = Math.max(...values, 1);
    return values.map((v, i) => ({
      ratio: max === 0 ? 0 : v / max,
      color:
        colors[
          Math.min(
            colors.length - 1,
            Math.floor((i / Math.max(1, values.length - 1)) * (colors.length - 1)),
          )
        ],
    }));
  }, [values, colors]);

  if (!bars.length) {
    return <div className={cn('bar-stack', className)} style={{ height }} aria-hidden />;
  }

  return (
    <div className={cn('bar-stack', className)} style={{ height }}>
      {bars.map((b, i) => (
        <span
          key={i}
          className={cn(
            'flex-1 rounded-[3px] transition-[height] duration-300',
            highlight === i && 'ring-2 ring-offset-1 ring-offset-bg-1 ring-text-1',
          )}
          style={{
            height: `${Math.max(3, b.ratio * height)}px`,
            background: b.ratio === 0 ? 'hsl(30 14% 90%)' : b.color,
          }}
        />
      ))}
    </div>
  );
}
